import cloneDeep from 'lodash/cloneDeep';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type {
	TranslationsState,
	TranslationResponse,
	TranslationPanelState,
	TranslationState,
	TranslationPanelMap,
	TranslationFieldState,
	TranslationPanel,
	TranslationGroupsState,
	ChangeType,
	FieldDataByIdMap,
} from '../../common/types/index.tsx';

export const getPanelIdentifier = (panel: TranslationPanel) => {
	if (panel.entityId) {
		return `${panel.providerUrlKey}-${panel.entityId}`;
	}
	return panel.providerUrlKey;
};

const getFormattedPanelData = (translationPanel: TranslationPanel): TranslationPanelState => {
	const panelId = getPanelIdentifier(translationPanel);

	let isPanelFlagged = false;

	const groups: TranslationGroupsState[] = translationPanel.translationGroups.map((group) => {
		const { translationFields } = group;

		const fields: TranslationFieldState[] = translationFields.map((translationField) => {
			if (translationField.translationState !== 'up_to_date') {
				isPanelFlagged = true;
			}

			return {
				...translationField,
				changeType: 'unchanged',
				isAlwaysUpToDate: false,
			};
		});

		return {
			...group,
			translationFields: fields,
		};
	});

	return {
		...translationPanel,
		translationGroups: groups,
		id: panelId,
		isDirty: false,
		isFlagged: isPanelFlagged,
	};
};

export const getUpdatedChangeType = ({
	isFieldChanged,
	isAlwaysUptoDate,
	originalApiFieldTranslationState,
}: {
	isFieldChanged: boolean;
	isAlwaysUptoDate: boolean;
	originalApiFieldTranslationState: TranslationState;
}): ChangeType => {
	if (isFieldChanged) {
		return 'changed';
	}
	if (isAlwaysUptoDate && originalApiFieldTranslationState === 'outdated') {
		return 'confirmed';
	}
	return 'unchanged';
};

export const getUpdatedTranslationState = ({
	isFieldChanged,
	isAlwaysUptoDate,
	originalApiFieldTranslationState,
	fieldContent,
}: {
	isFieldChanged: boolean;
	isAlwaysUptoDate: boolean;
	originalApiFieldTranslationState: TranslationState;
	fieldContent: string;
}): TranslationState => {
	if (!fieldContent) {
		return 'missing';
	}
	if (originalApiFieldTranslationState === 'outdated' && !isFieldChanged && !isAlwaysUptoDate) {
		return 'outdated';
	}
	return 'up_to_date';
};

export const getUpdatedFieldStates = ({
	cachedOriginalApiFieldsMap,
	field,
	fieldContent,
	currentLanguage,
}: {
	cachedOriginalApiFieldsMap: FieldDataByIdMap;
	field: TranslationFieldState;
	fieldContent: string;
	currentLanguage: string;
}) => {
	const cachedOriginalApiField = cachedOriginalApiFieldsMap?.[field.logicalId];
	// if field is changed before then the original api values will be in cachedOriginalApiField
	const originalApiField = cachedOriginalApiField || field;

	const originalApiFieldContent = originalApiField.translationContentMap[currentLanguage];

	const isFieldChanged = fieldContent !== originalApiFieldContent;

	const isAlwaysUptoDate =
		originalApiField.translationState === 'up_to_date' || field.isAlwaysUpToDate;

	const updatedTranslationState = getUpdatedTranslationState({
		isFieldChanged,
		isAlwaysUptoDate,
		originalApiFieldTranslationState: originalApiField.translationState,
		fieldContent,
	});
	const updatedChangeType = getUpdatedChangeType({
		isFieldChanged,
		isAlwaysUptoDate,
		originalApiFieldTranslationState: originalApiField.translationState,
	});

	const isFieldDirty = updatedChangeType === 'changed' || updatedChangeType === 'confirmed';

	return {
		isFieldDirty,
		updatedTranslationState,
		updatedChangeType,
	};
};

const getFormattedData = (translationsData: TranslationResponse) => {
	const translationPanelMap: TranslationPanelMap = {};
	const { defaultLanguage, selectedLanguage } = translationsData;

	const translationTabsWithPanelIds = translationsData.translationTabs.map((translationTab) => {
		const { translationPanels, key, name } = translationTab;
		const translationPanelIds = translationPanels.map((translationPanel) => {
			const panel = getFormattedPanelData(translationPanel);
			translationPanelMap[panel.id] = panel;
			return panel.id;
		});

		return {
			key,
			name,
			translationPanelIds,
		};
	});

	return {
		translationPanelMap,
		translationTabsWithPanelIds,
		selectedLanguage,
		defaultLanguage,
	};
};

export const actions = {
	initialize:
		(
			translationsData: TranslationResponse | null,
			error: Error | null,
			loading: boolean,
		): Action<TranslationsState> =>
		({ setState, getState }) => {
			if (loading) {
				return;
			}

			if (!translationsData) {
				return;
			}

			const {
				translationTabsWithPanelIds,
				translationPanelMap,
				defaultLanguage,
				selectedLanguage,
			} = getFormattedData(translationsData);

			setState({
				...getState(),
				currentLanguage: translationsData?.selectedLanguage.key,
				loading,
				error,
				translationPanelMap,
				translationTabsWithPanelIds,
				selectedLanguage,
				defaultLanguage,
			});
		},
	resetSelectedLanguage:
		(): Action<TranslationsState> =>
		({ getState, setState }) => {
			setState({
				...getState(),
				...{
					selectedLanguage: null,
				},
			});
		},
	updateFields:
		({
			fieldLogicalId,
			fieldContent,
		}: {
			fieldLogicalId: string;
			fieldContent: string;
		}): Action<TranslationsState> =>
		({ getState, dispatch }) => {
			const { currentLanguage, cachedOriginalApiFieldsMap, selectedPanelId, translationPanelMap } =
				getState();

			if (translationPanelMap === null || selectedPanelId === null) {
				return;
			}

			const selectedPanel = translationPanelMap[selectedPanelId];

			let updatedPanelFlaggedStatus = false;
			let isDirty = false;

			const updatedTranslationGroups: TranslationGroupsState[] =
				selectedPanel.translationGroups.map((translationGroup) => ({
					...translationGroup,
					translationFields: translationGroup.translationFields.map((field) => {
						// field currently getting updated
						if (field.logicalId === fieldLogicalId) {
							const cachedOriginalApiField = cachedOriginalApiFieldsMap?.[field.logicalId];

							const { updatedChangeType, updatedTranslationState, isFieldDirty } =
								getUpdatedFieldStates({
									cachedOriginalApiFieldsMap,
									field,
									currentLanguage,
									fieldContent,
								});

							if (updatedTranslationState !== 'up_to_date') {
								updatedPanelFlaggedStatus = true;
							}

							isDirty = isDirty || isFieldDirty;

							// save the field in cachedOriginalApiFieldMap if it is not present
							if (!cachedOriginalApiField) {
								dispatch(actions.setOriginalApiFieldCache(field));
							}

							return {
								...field,
								changeType: updatedChangeType,
								translationState: updatedTranslationState,
								translationContentMap: {
									...field.translationContentMap,
									[currentLanguage]: fieldContent,
								},
							};
						}
						if (field.translationState !== 'up_to_date') {
							updatedPanelFlaggedStatus = true;
						}
						const { changeType } = field;
						const isFieldDirty = changeType === 'changed' || changeType === 'confirmed';
						isDirty = isDirty || isFieldDirty;
						return field;
					}),
				}));

			const updatedSelectedPanel: TranslationPanelState = {
				...selectedPanel,
				isDirty,
				isFlagged: updatedPanelFlaggedStatus,
				translationGroups: updatedTranslationGroups,
			};

			dispatch(actions.updateSelectedPanelData(selectedPanelId, updatedSelectedPanel));
		},

	discardChanges:
		(): Action<TranslationsState> =>
		({ getState, dispatch }) => {
			const { cachedOriginalApiFieldsMap, selectedPanelId, translationPanelMap } = getState();

			if (translationPanelMap === null || selectedPanelId === null) {
				return;
			}
			const selectedPanel = translationPanelMap[selectedPanelId];

			let updatedPanelFlaggedStatus = false;

			const updatedTranslationGroups: TranslationGroupsState[] =
				selectedPanel.translationGroups.map((translationGroups) => ({
					...translationGroups,
					translationFields: translationGroups.translationFields.map((field) => {
						// take the cachedOriginalApiFieldsMap data if present for the field
						const originalField = cachedOriginalApiFieldsMap?.[field.logicalId] || field;

						if (originalField.translationState !== 'up_to_date') {
							updatedPanelFlaggedStatus = true;
						}
						return {
							...originalField,
							changeType: 'unchanged',
							isAlwaysUpToDate: false,
							translationContentMap: {
								...originalField.translationContentMap,
							},
						};
					}),
				}));

			const updatedSelectedPanel: TranslationPanelState = {
				...selectedPanel,
				isDirty: false,
				translationGroups: updatedTranslationGroups,
				isFlagged: updatedPanelFlaggedStatus,
			};

			dispatch(actions.updateSelectedPanelData(selectedPanelId, updatedSelectedPanel));
			dispatch(actions.resetOriginalApiFieldCache());
		},

	setSelectedPanelId:
		(selectedPanelId: string): Action<TranslationsState> =>
		({ getState, setState }) => {
			setState({ ...getState(), selectedPanelId });
		},

	confirmTranslation:
		({ fieldLogicalId }: { fieldLogicalId: string }): Action<TranslationsState> =>
		({ getState, dispatch }) => {
			const { selectedPanelId, translationPanelMap, cachedOriginalApiFieldsMap } = getState();

			if (translationPanelMap === null || selectedPanelId === null) {
				return;
			}

			const selectedPanel = translationPanelMap[selectedPanelId];

			let isPanelFlaggedStatusUpdated = false;

			const updatedTranslationGroups: TranslationGroupsState[] =
				selectedPanel.translationGroups.map((translationGroup) => ({
					...translationGroup,
					translationFields: translationGroup.translationFields.map((field) => {
						// field currently getting updated
						if (field.logicalId === fieldLogicalId) {
							const cachedOriginalApiField: TranslationFieldState | undefined =
								cachedOriginalApiFieldsMap?.[field.logicalId];

							if (!cachedOriginalApiField) {
								dispatch(actions.setOriginalApiFieldCache(field));
							}

							return {
								...field,
								isAlwaysUpToDate: true,
								translationState: 'up_to_date',
								changeType: 'confirmed',
							};
						}
						if (field.translationState !== 'up_to_date') {
							isPanelFlaggedStatusUpdated = true;
						}

						return field;
					}),
				}));

			const updatedSelectedPanel: TranslationPanelState = {
				...selectedPanel,
				isDirty: true,
				isFlagged: isPanelFlaggedStatusUpdated,
				translationGroups: updatedTranslationGroups,
			};

			dispatch(actions.updateSelectedPanelData(selectedPanelId, updatedSelectedPanel));
		},

	setOriginalApiFieldCache:
		(field: TranslationFieldState): Action<TranslationsState> =>
		({ getState, setState }) => {
			const { cachedOriginalApiFieldsMap } = getState();
			const updatedCachedOriginalApiFieldsMap = {
				...cachedOriginalApiFieldsMap,
				[field.logicalId]: {
					...field,
					translationContentMap: {
						...field.translationContentMap,
					},
				},
			};
			setState({
				...getState(),
				cachedOriginalApiFieldsMap: updatedCachedOriginalApiFieldsMap,
			});
		},

	updateSelectedPanelData:
		(
			selectedPanelId: string,
			updatedSelectedPanel: TranslationPanelState,
		): Action<TranslationsState> =>
		({ getState, setState }) => {
			const { translationPanelMap } = getState();
			setState({
				...getState(),
				translationPanelMap: {
					...translationPanelMap,
					[selectedPanelId]: updatedSelectedPanel,
				},
			});
		},

	resetOriginalApiFieldCache:
		(): Action<TranslationsState> =>
		({ getState, setState }) => {
			setState({ ...getState(), cachedOriginalApiFieldsMap: null });
		},

	onFooterSaveInitiated:
		(): Action<TranslationsState> =>
		({ getState, setState }) => {
			const { cachedOriginalApiFieldsMap, selectedPanelId, translationPanelMap } = getState();

			if (selectedPanelId === null || translationPanelMap === null) {
				return;
			}

			const oldApiFieldsMap = cloneDeep(cachedOriginalApiFieldsMap);

			const selectedPanel = translationPanelMap[selectedPanelId];
			const panelDataReset = getFormattedPanelData(selectedPanel);
			setState({
				oldApiFieldsMap,
				cachedOriginalApiFieldsMap: null,
				translationPanelMap: {
					...translationPanelMap,
					[selectedPanelId]: panelDataReset,
				},
			});
		},
	onFooterSaveFailure:
		(): Action<TranslationsState> =>
		({ getState, setState }) => {
			const { selectedPanelId, translationPanelMap, currentLanguage, oldApiFieldsMap } = getState();

			if (translationPanelMap === null || selectedPanelId === null) {
				return;
			}

			const cachedOriginalApiFieldsMap = cloneDeep(oldApiFieldsMap);

			const selectedPanel = translationPanelMap[selectedPanelId];

			let isPanelFlagged = false;
			let isDirty = false;

			const updatedTranslationGroups: TranslationGroupsState[] =
				selectedPanel.translationGroups.map((translationGroup) => ({
					...translationGroup,
					translationFields: translationGroup.translationFields.map((field) => {
						const fieldContent = field.translationContentMap[currentLanguage];

						const { updatedChangeType, updatedTranslationState, isFieldDirty } =
							getUpdatedFieldStates({
								cachedOriginalApiFieldsMap,
								field,
								currentLanguage,
								fieldContent,
							});

						if (updatedTranslationState !== 'up_to_date') {
							isPanelFlagged = true;
						}

						isDirty = isDirty || isFieldDirty;
						return {
							...field,
							changeType: updatedChangeType,
							translationState: updatedTranslationState,
						};
					}),
				}));

			const updatedSelectedPanel: TranslationPanelState = {
				...selectedPanel,
				isDirty,
				isFlagged: isPanelFlagged,
				translationGroups: updatedTranslationGroups,
			};

			setState({
				cachedOriginalApiFieldsMap,
				oldApiFieldsMap: null,
				translationPanelMap: {
					...translationPanelMap,
					[selectedPanelId]: updatedSelectedPanel,
				},
			});
		},

	onFooterSaveSuccess:
		(): Action<TranslationsState> =>
		({ getState, setState }) => {
			setState({
				...getState(),
				oldApiFieldsMap: null,
			});
		},

	resetStoreAfterSave:
		(): Action<TranslationsState> =>
		({ getState, setState }) => {
			const { selectedPanelId, translationPanelMap } = getState();
			if (translationPanelMap === null || selectedPanelId === null) {
				return;
			}

			const selectedPanel = translationPanelMap[selectedPanelId];

			const panelDataReset = getFormattedPanelData(selectedPanel);
			setState({
				...getState(),
				cachedOriginalApiFieldsMap: null,
				translationPanelMap: {
					...translationPanelMap,
					[selectedPanelId]: panelDataReset,
				},
			});
		},
};
